import { Button } from "@mui/material";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { HOTEL_CLASSES } from "@/app/lib/constants";
import { useToggleSelectAll } from "@/app/lib/hooks/useToggleSelectAll";
import { DrawerTopBarWithAction } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { HotelClassFilter } from "@/features/hotels/components/hotels-list/HotelClassFilter";

type HotelClassFilterContentsProps = {
  defaultFilter?: number[];
  value?: number[];
  onSubmit: (newValue: number[]) => void;
  onCloseClick: () => void;
};

export const HotelClassFilterContents = ({
  defaultFilter = [],
  value = [],
  onSubmit,
  onCloseClick,
}: HotelClassFilterContentsProps) => {
  const t = useTranslations();

  const [localFilter, setLocalFilter] = useState(value);

  const {
    manuallySelectedAll,
    setManuallySelectedAll,
    areAllSelected,
    toggle,
  } = useToggleSelectAll<number>({
    selected: localFilter,
    all: HOTEL_CLASSES,
  });

  const isInDefaultState = areAllSelected && !manuallySelectedAll;

  return (
    <>
      <DrawerTopBarWithAction
        onCloseClick={onCloseClick}
        title={t("hotels.filters.hotelClass")}
        actionElement={
          <Button
            onClick={(e) => {
              if (isInDefaultState) {
                setLocalFilter(HOTEL_CLASSES);
                setManuallySelectedAll(true);
              } else {
                setLocalFilter(defaultFilter);
                setManuallySelectedAll(false);
              }

              // Remove focus from the button after click
              e.currentTarget.blur();
            }}
            variant="text"
            size="small"
          >
            {isInDefaultState ? t("selectAll") : t("clearFilter")}
          </Button>
        }
      />

      <HotelClassFilter
        selectedClasses={localFilter}
        userManuallySelectedAllClasses={manuallySelectedAll}
        onChange={(_e, checked, option) => {
          setLocalFilter((prevFilter) => {
            const newClasses = toggle(prevFilter, option, checked);
            return newClasses.sort((a, b) => a - b);
          });
        }}
      />

      <div className="mt-18">
        <Button
          fullWidth
          onClick={() => {
            onSubmit(localFilter);
            onCloseClick();
          }}
          variant="contained"
          size="large"
        >
          {t("apply")}
        </Button>
      </div>
    </>
  );
};

type Props = HotelClassFilterContentsProps & {
  open: boolean;
};

export const HotelClassFilterDrawer = ({
  open,
  defaultFilter = [],
  value = [],
  onSubmit,
  onCloseClick,
}: Props) => {
  return (
    <SwipeableDrawer open={open} onClose={onCloseClick}>
      {/* need to reset the state when drawer is open/closed */}
      {open ? (
        <HotelClassFilterContents
          defaultFilter={defaultFilter}
          value={value}
          onSubmit={onSubmit}
          onCloseClick={onCloseClick}
        />
      ) : null}
    </SwipeableDrawer>
  );
};
